import React, { useState } from 'react';
import { Circle } from 'lucide-react';
import { ErrorNotification, ErrorMessage, createErrorMessage } from './ErrorNotification';
import { restClient } from './RestClient';

interface LoginProps {
  onLogin: (token: string, username: string, isAdmin: boolean, isSuperUser: boolean) => void;
  onSwitchToSignUp: () => void;
  onSwitchToForgotPassword: () => void;
}

interface LoginResponse {
  token: string;
  username: string;
  nickname: string;
  expiresAt: string;
  isAdmin: boolean;
  isSuperUser: boolean;
}

export function Login({ onLogin, onSwitchToSignUp, onSwitchToForgotPassword }: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);

  const addError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const error = createErrorMessage(message, type);
    setErrors(prev => [...prev, error]);

    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, 8000);
  };

  const dismissError = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      addError('Please enter both username and password');
      return;
    }

    setIsLoading(true);

    try {
      const result = await restClient.login(username, password);

      if (result.IsSuccess) {
        const data: LoginResponse = result.Value;
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('nickname', data.nickname);
        localStorage.setItem('tokenExpiry', data.expiresAt);
        localStorage.setItem('isAdmin', data.isAdmin.toString());
        localStorage.setItem('isSuperUser', data.isSuperUser.toString());
        onLogin(data.token, data.username, data.isAdmin, data.isSuperUser);
      } else {
        addError(result.Error.description || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      addError('Unable to connect to server. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <ErrorNotification errors={errors} onDismiss={dismissError} />

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo auth-logo-login">
            <Circle size={28} fill="currentColor" />
          </div>
          <h1 className="auth-title">Team Selection</h1>
          <p className="auth-subtitle">Please sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="mb-6">
          <div className="form-group-lg">
            <label className="form-label">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={isLoading}
              className="form-control form-control-lg"
            />
          </div>

          <div className="form-group-lg">
            <label className="form-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isLoading}
              className="form-control form-control-lg"
            />
          </div>

          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={onSwitchToForgotPassword}
              disabled={isLoading}
              className="btn-link text-sm"
              style={{ opacity: isLoading ? 0.5 : 1 }}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`btn btn-lg btn-full ${isLoading ? 'btn-secondary' : 'btn-primary'}`}
          >
            {isLoading ? (
              <>
                <span className="spinner" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p className="auth-footer-text">Don't have an account?</p>
          <button
            onClick={onSwitchToSignUp}
            disabled={isLoading}
            className="btn-link btn-link-success"
            style={{ opacity: isLoading ? 0.5 : 1 }}
          >
            Create an account
          </button>
        </div>
      </div>
    </div>
  );
}
